import {
  Box,
  Card,
  FormControl,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { FinancialStatements } from '@akem/shared';
import { getProfileFinancialStatements } from '../../../services/profile.js';
import type { WidgetProps } from '../types/index.js';

type StatementTab = 'income' | 'balance' | 'cashflow';
type Timeframe = 'quarterly' | 'annual';

const formatPeriod = (periodEnd: string, fiscalQuarter?: number, fiscalYear?: number): string => {
  if (fiscalQuarter && fiscalYear) {
    return `Q${fiscalQuarter} ${fiscalYear}`;
  }
  if (fiscalYear) {
    return `${fiscalYear}`;
  }
  // Fallback: parse date like "2024-12-28" -> "Dec 2024"
  if (periodEnd) {
    const date = new Date(periodEnd);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    }
  }
  return periodEnd;
};

const formatValue = (value: number | undefined): string => {
  if (value === undefined || value === 0) return '-';
  const absValue = Math.abs(value);
  let formatted: string;
  if (absValue >= 1e12) {
    formatted = `${(absValue / 1e12).toFixed(2)}T`;
  } else if (absValue >= 1e9) {
    formatted = `${(absValue / 1e9).toFixed(2)}B`;
  } else if (absValue >= 1e6) {
    formatted = `${(absValue / 1e6).toFixed(2)}M`;
  } else if (absValue >= 1e3) {
    formatted = `${(absValue / 1e3).toFixed(2)}K`;
  } else {
    formatted = `${absValue.toFixed(2)}`;
  }
  return value < 0 ? `(${formatted})` : formatted;
};

export const FinancialStatementsWidget: React.FC<WidgetProps> = ({ widget }) => {
  const profileId = (widget.config as any)?.profileId as string | undefined;
  const [data, setData] = React.useState<FinancialStatements | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [activeTab, setActiveTab] = React.useState<StatementTab>('income');
  const [timeframe, setTimeframe] = React.useState<Timeframe>('quarterly');

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        if (!profileId) {
          setData(null);
          setLoading(false);
          return;
        }

        const financials = await getProfileFinancialStatements(profileId);
        if (cancelled) return;

        if (financials) {
          setData(financials);
        } else {
          setData(null);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load financial statements:', err);
          setError(err instanceof Error ? err.message : 'Failed to load financial statements');
          setData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [profileId]);

  if (loading) {
    return (
      <Card sx={{ p: 2, height: '100%' }}>
        <Skeleton variant="rectangular" height={400} />
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card sx={{ p: 2, height: '100%' }}>
        <Typography color="error">{error || 'No financial data available'}</Typography>
      </Card>
    );
  }

  // Filter by timeframe (only show last 4 quarters for quarterly)
  const filteredIncome = timeframe === 'quarterly'
    ? data.incomeStatement.filter(p => p.timeframe === 'quarterly').slice(0, 4).reverse()
    : data.incomeStatement.filter(p => p.timeframe === 'annual').slice(0, 4).reverse();
  
  const filteredBalance = timeframe === 'quarterly'
    ? data.balanceSheet.filter(p => p.timeframe === 'quarterly').slice(0, 4).reverse()
    : data.balanceSheet.filter(p => p.timeframe === 'annual').slice(0, 4).reverse();
  
  const filteredCashFlow = timeframe === 'quarterly'
    ? data.cashFlow.filter(p => p.timeframe === 'quarterly').slice(0, 4).reverse()
    : data.cashFlow.filter(p => p.timeframe === 'annual').slice(0, 4).reverse();

  // Chart data
  const incomeChartData = filteredIncome.map(p => ({
    name: formatPeriod(p.period_end, p.fiscal_quarter, p.fiscal_year),
    Revenue: (p.revenue / 1e9).toFixed(2),
    'Net Income': (p.net_income / 1e9).toFixed(2),
  }));

  const balanceChartData = filteredBalance.map(p => ({
    name: formatPeriod(p.period_end, p.fiscal_quarter, p.fiscal_year),
    'Total Assets': (p.total_assets / 1e9).toFixed(2),
    'Total Liabilities': (p.total_liabilities / 1e9).toFixed(2),
  }));

  const cashFlowChartData = filteredCashFlow.map(p => ({
    name: formatPeriod(p.period_end, p.fiscal_quarter, p.fiscal_year),
    'Cash from Operations': (p.net_cash_from_operating_activities / 1e9).toFixed(2),
    'Cash from Investing': (p.net_cash_from_investing_activities / 1e9).toFixed(2),
    'Cash from Financing': (p.net_cash_from_financing_activities / 1e9).toFixed(2),
  }));

  return (
    <Card sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Stack spacing={2}>
        {/* Header with tabs and timeframe selector */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ minHeight: 36 }}>
            <Tab label="Income statement" value="income" sx={{ minHeight: 36, py: 1 }} />
            <Tab label="Balance sheet" value="balance" sx={{ minHeight: 36, py: 1 }} />
            <Tab label="Cash flow" value="cashflow" sx={{ minHeight: 36, py: 1 }} />
          </Tabs>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select value={timeframe} onChange={(e) => setTimeframe(e.target.value as Timeframe)}>
              <MenuItem value="quarterly">Quarterly</MenuItem>
              <MenuItem value="annual">Annual</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Income Statement */}
        {activeTab === 'income' && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" gutterBottom>Income statement</Typography>
              <Stack direction="row" spacing={1} mb={2}>
                <Box sx={{ width: 12, height: 12, bgcolor: '#5b8ff9', borderRadius: '2px', mt: 0.5 }} />
                <Typography variant="body2">Revenue</Typography>
                <Box sx={{ width: 12, height: 12, bgcolor: '#9ba7ff', borderRadius: '2px', mt: 0.5, ml: 2 }} />
                <Typography variant="body2">Net income</Typography>
              </Stack>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={incomeChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="name" stroke="#888" />
                  <YAxis stroke="#888" />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1e1e1e', border: '1px solid #444' }}
                    labelStyle={{ color: '#fff' }}
                  />
                  <Bar dataKey="Revenue" fill="#5b8ff9" />
                  <Bar dataKey="Net Income" fill="#9ba7ff" />
                </BarChart>
              </ResponsiveContainer>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    {filteredIncome.map((p, i) => (
                      <TableCell key={i} align="right" sx={{ fontWeight: 600 }}>
                        {formatPeriod(p.period_end, p.fiscal_quarter, p.fiscal_year)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Revenue</TableCell>
                    {filteredIncome.map((p, i) => (
                      <TableCell key={i} align="right">{formatValue(p.revenue)}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>Cost of goods sold</TableCell>
                    {filteredIncome.map((p, i) => (
                      <TableCell key={i} align="right">{formatValue(p.cost_of_revenue)}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>Cost of revenue</TableCell>
                    {filteredIncome.map((p, i) => (
                      <TableCell key={i} align="right">{formatValue(p.cost_of_revenue)}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>Research and development expenses</TableCell>
                    {filteredIncome.map((p, i) => (
                      <TableCell key={i} align="right">{formatValue(p.research_development)}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>Selling, general, and admin expenses</TableCell>
                    {filteredIncome.map((p, i) => (
                      <TableCell key={i} align="right">{formatValue(p.selling_general_administrative)}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>Operating expense</TableCell>
                    {filteredIncome.map((p, i) => (
                      <TableCell key={i} align="right">{formatValue(p.total_operating_expenses)}</TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}

        {/* Balance Sheet */}
        {activeTab === 'balance' && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" gutterBottom>Balance sheet</Typography>
              <Stack direction="row" spacing={1} mb={2}>
                <Box sx={{ width: 12, height: 12, bgcolor: '#5b8ff9', borderRadius: '2px', mt: 0.5 }} />
                <Typography variant="body2">Total assets</Typography>
                <Box sx={{ width: 12, height: 12, bgcolor: '#9ba7ff', borderRadius: '2px', mt: 0.5, ml: 2 }} />
                <Typography variant="body2">Total liabilities</Typography>
              </Stack>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={balanceChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="name" stroke="#888" />
                  <YAxis stroke="#888" />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1e1e1e', border: '1px solid #444' }}
                    labelStyle={{ color: '#fff' }}
                  />
                  <Bar dataKey="Total Assets" fill="#5b8ff9" />
                  <Bar dataKey="Total Liabilities" fill="#9ba7ff" />
                </BarChart>
              </ResponsiveContainer>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    {filteredBalance.map((p, i) => (
                      <TableCell key={i} align="right" sx={{ fontWeight: 600 }}>
                        {formatPeriod(p.period_end, p.fiscal_quarter, p.fiscal_year)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Cash and equivalents</TableCell>
                    {filteredBalance.map((p, i) => (
                      <TableCell key={i} align="right">{formatValue(p.cash_and_equivalents)}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>Short term investments</TableCell>
                    {filteredBalance.map((p, i) => (
                      <TableCell key={i} align="right">{formatValue(p.short_term_investments)}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>Cash and short-term investments</TableCell>
                    {filteredBalance.map((p, i) => (
                      <TableCell key={i} align="right">
                        {formatValue((p.cash_and_equivalents || 0) + (p.short_term_investments || 0))}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>Total receivables</TableCell>
                    {filteredBalance.map((p, i) => (
                      <TableCell key={i} align="right">{formatValue(p.receivables)}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>Inventory</TableCell>
                    {filteredBalance.map((p, i) => (
                      <TableCell key={i} align="right">{formatValue(p.inventories)}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>Total other current assets</TableCell>
                    {filteredBalance.map((p, i) => (
                      <TableCell key={i} align="right">{formatValue(p.other_current_assets)}</TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}

        {/* Cash Flow */}
        {activeTab === 'cashflow' && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" gutterBottom>Cash flow</Typography>
              <Stack direction="row" spacing={1} mb={2}>
                <Box sx={{ width: 12, height: 12, bgcolor: '#5b8ff9', borderRadius: '2px', mt: 0.5 }} />
                <Typography variant="body2">Cash from operations</Typography>
                <Box sx={{ width: 12, height: 12, bgcolor: '#9ba7ff', borderRadius: '2px', mt: 0.5, ml: 2 }} />
                <Typography variant="body2">Cash from investing</Typography>
                <Box sx={{ width: 12, height: 12, bgcolor: 'transparent', border: '2px solid #666', borderRadius: '2px', mt: 0.5, ml: 2 }} />
                <Typography variant="body2">Cash from financing</Typography>
              </Stack>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={cashFlowChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="name" stroke="#888" />
                  <YAxis stroke="#888" />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1e1e1e', border: '1px solid #444' }}
                    labelStyle={{ color: '#fff' }}
                  />
                  <Bar dataKey="Cash from Operations" fill="#5b8ff9" />
                  <Bar dataKey="Cash from Investing" fill="#9ba7ff" />
                  <Bar dataKey="Cash from Financing" fill="transparent" stroke="#666" strokeWidth={2} />
                </BarChart>
              </ResponsiveContainer>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    {filteredCashFlow.map((p, i) => (
                      <TableCell key={i} align="right" sx={{ fontWeight: 600 }}>
                        {formatPeriod(p.period_end, p.fiscal_quarter, p.fiscal_year)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Net income</TableCell>
                    {filteredCashFlow.map((p, i) => (
                      <TableCell key={i} align="right">{formatValue(p.net_income)}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Cash from operations</TableCell>
                    {filteredCashFlow.map((p, i) => (
                      <TableCell key={i} align="right">{formatValue(p.net_cash_from_operating_activities)}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>Change in inventories</TableCell>
                    {filteredCashFlow.map((p, i) => (
                      <TableCell key={i} align="right">{formatValue(p.change_in_other_operating_assets_and_liabilities_net)}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Cash from investing</TableCell>
                    {filteredCashFlow.map((p, i) => (
                      <TableCell key={i} align="right">{formatValue(p.net_cash_from_investing_activities)}</TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}
      </Stack>
    </Card>
  );
};
